'use client';

import { useEffect, useRef } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { FeaturesListType } from '@/types/ui';
import PhoneContactScreen from './PhoneContactScreen';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function HowItWorks() {
    const t = useTranslations('HowItWorks');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => {
                            container.scrollTo({ top: 70, behavior: 'smooth' });
                            setTimeout(() => {
                                container.scrollTo({ top: 0, behavior: 'smooth' });
                            }, 600);
                        }, 500);
                        observer.disconnect(); // Run animation only once
                    }
                });
            },
            { threshold: 0.3 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const steps: FeaturesListType[] = [
        {
            title: 'step_1_title',
            description: 'step_1_desc',
            icon: null
        },
        {
            title: 'step_2_title',
            description: 'step_2_desc',
            icon: null
        },
        {
            title: 'step_3_title',
            description: 'step_3_desc',
            icon: null
        },
        {
            title: 'step_4_title',
            description: 'step_4_desc',
            icon: null
        },
        {
            title: 'step_5_title',
            description: 'step_5_desc',
            icon: null
        },
    ];

    return (
        <div
            className="w-full bg-white py-16 sm:py-24 px-6 sm:px-10 lg:px-16"
            id="how_it_works"
        >
            <div className="max-w-8xl mx-auto">
                <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                    <span className="text-xs sm:text-sm font-semibold tracking-widest color-primary uppercase">
                        {t('how_it_works')}
                    </span>
                    <h2 className="text-3xl sm:text-5xl font-semibold text-black tracking-tight mt-2">
                        {t('title')}
                    </h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
                    <div className="bg-[#007AFF] bg-gradient-to-b from-[#0A2540] to-[#007AFF] rounded-3xl flex justify-center items-end relative overflow-hidden min-h-[480px]">
                        <div className="relative w-full max-w-[500px] aspect-[18/18] transform translate-y-6">
                            <PhoneContactScreen />
                        </div>
                    </div>
                    <div className="bg-light rounded-3xl p-8 sm:p-12 flex flex-col justify-between relative">
                        <div className="mb-6 shrink-0">
                            <h3 className="text-3xl sm:text-4xl font-semibold text-black tracking-tight leading-tight">
                                {t.rich('subtitle', {
                                    highlight: (chunks) => <span className="color-primary">{chunks}</span>,
                                })}
                            </h3>
                            <p className="text-sm mt-3 max-w-md font-medium leading-relaxed">
                                {t('description')}
                            </p>
                        </div>
                        <div
                            className="space-y-4 pr-2 rounded-2xl"
                        >
                            {steps.map((step: FeaturesListType, index: number) => (
                                <div
                                    key={index}
                                    className="bg-white flex gap-3 items-center rounded-2xl px-4 py-3 shadow-sm border border-blue-50/50 transition-all hover:shadow-md"
                                >
                                    <span className={`flex gap-1 min-w-[70px] ${index === 4 ? 'bg-green-500' : 'bg-primary'} text-white text-xs font-bold px-3 py-1 rounded-full mb-3 justify-center`}>
                                        {t('step')}{" "}{index + 1}
                                    </span>
                                    <div>
                                        <h4 className={`text-base font-bold ${index === 4 ? 'text-green-500' : 'color-primary'} mb-1/2`}>
                                            {t(step.title)}
                                        </h4>
                                        <p className="text-xs sm:text-sm leading-relaxed">
                                            {t(step.description)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}